import { PaymentOption } from "@paywithglide/glide-js";
import { formatDisplayAmount } from "./../lib/formatDisplayAmount";
import TransactionCheckoutHeader from "./../components/TransactionCheckoutHeader";
import ModalBackButton from "./../components/ModalBackButton";
import { useState, useEffect } from "react";
import SecondaryButton from "../components/SecondaryButton";
import { ChevronDown, ChevronUp } from "lucide-react";
import { LogoWithBadge } from "../components/LogoWithBadge";
import { SectionTitle } from "../components/SectionTitle";
import { useGlideDepositConfig } from "../lib/useDepositConfig";
import { useConnectedWallet } from "../lib/useConnectedWallet";
import { useAccount, useDisconnect } from "wagmi";
import { useWallets } from "../lib/connectkit/wallets/useWallets";
import { formatShortWalletAddress } from "../lib/formatShortWalletAddress";
import { useAccount as useSolanaAccount } from "../lib/solana/useAccount";
import { getWallets as getSolanaWallets } from "@wallet-standard/app";
import { glideLocalStorage } from "../lib/glideLocalStorage";
import { logger } from "../lib/logger";
import EVMWalletsLogo from "../logos/EVMWalletsLogo";
import { DisconnectWalletButton } from "../components/DisconnectWalletButton";
import { saveTokenSelectionFromPaymentOption } from "../lib/tokenSelectionStorage";

type Props = {
  paymentMethod: "wallet" | "transfer" | "coinbase_app";
  paymentOptions: PaymentOption[];
  onSelect: (paymentOption: PaymentOption) => void;
  onBack: () => void;
  onReset?: () => void;
};

export default function TransactionCheckoutPaymentOptionsList({
  paymentMethod,
  paymentOptions,
  onSelect,
  onBack,
  onReset,
}: Props) {
  const depositConfig = useGlideDepositConfig();
  const { isConnected, address: connectedAddress } = useConnectedWallet();
  const { connector: evmConnector } = useAccount();
  const evmWallets = useWallets();
  const { address: solanaAddress } = useSolanaAccount();
  const { disconnectAsync } = useDisconnect();

  const [selectedPaymentOption, setSelectedPaymentOption] =
    useState<PaymentOption>();
  const [expandTokenList, setExpandTokenList] = useState(false);
  const [externalWalletEcosystem, setExternalWalletEcosystem] = useState<
    "evm" | "solana" | undefined
  >(
    localStorage.getItem("glide.externalWalletEcosystem") === "solana"
      ? "solana"
      : localStorage.getItem("glide.externalWalletEcosystem") === "evm"
        ? "evm"
        : undefined,
  );
  const [solanaConnectorName, setSolanaConnectorName] = useState<
    string | undefined
  >(localStorage.getItem("glide.solana.connector") || undefined);

  useEffect(() => {
    const loadEcosystem = async () => {
      const ecosystem = await glideLocalStorage.getItem(
        "glide.externalWalletEcosystem",
      );
      setExternalWalletEcosystem(
        ecosystem === "solana"
          ? "solana"
          : ecosystem === "evm"
            ? "evm"
            : undefined,
      );

      const solanaConnector = await glideLocalStorage.getItem(
        "glide.solana.connector",
      );
      setSolanaConnectorName(solanaConnector || undefined);
    };
    loadEcosystem();
  }, []);

  // Disconnect handler
  const handleDisconnect = async () => {
    try {
      logger.info("Starting wallet disconnect...");
      setExternalWalletEcosystem(undefined);
      setSolanaConnectorName(undefined);

      await glideLocalStorage.removeItem("glide.useExternalWallet");
      await glideLocalStorage.removeItem("glide.useParentWallet");
      await glideLocalStorage.removeItem("glide.externalWalletEcosystem");
      await glideLocalStorage.removeItem("glide.solana.connector");
      await glideLocalStorage.removeItem("glide.solana.account");
      await glideLocalStorage.removeItem("glide.wagmi.recentConnectorId");

      await disconnectAsync();
      onReset?.();

      logger.info("Wallet disconnected successfully");
    } catch (error) {
      logger.error(`Failed to disconnect wallet: ${error}`);
    }
  };

  // Get wallet info (only for wallet payment method)
  const connectedWallet = (() => {
    if (paymentMethod !== "wallet" || !isConnected || !connectedAddress)
      return null;

    // External Solana wallet
    if (externalWalletEcosystem === "solana" && solanaAddress) {
      const wallets = getSolanaWallets().get();
      const wallet = wallets.find((w) => w.name === solanaConnectorName);
      return {
        name: wallet?.name || "Solana Wallet",
        icon: wallet?.icon ? (
          <img src={wallet.icon} alt={wallet.name} className="h-8 w-8" />
        ) : null,
        address: solanaAddress,
      };
    }

    // External EVM wallet
    if (externalWalletEcosystem === "evm" && evmConnector) {
      const wallet = evmWallets.find((w) => w.connector.id === evmConnector.id);

      let icon = null;
      if (wallet?.icon) {
        icon = <div className="h-8 w-8">{wallet.icon}</div>;
      } else if (evmConnector.icon) {
        icon = (
          <img
            src={evmConnector.icon}
            alt={evmConnector.name}
            className="h-8 w-8"
          />
        );
      }

      return {
        name: wallet?.name || evmConnector.name || "Wallet",
        icon,
        address: connectedAddress,
      };
    }

    // Parent app wallet (no externalWalletEcosystem set)
    if (!externalWalletEcosystem) {
      return {
        name: depositConfig.appName || "Parent Wallet",
        icon: depositConfig.appFaviconUrl ? (
          <img
            src={depositConfig.appFaviconUrl}
            alt="App icon"
            className="h-8 w-8 object-contain"
          />
        ) : null,
        address: connectedAddress,
      };
    }

    return null;
  })();

  const onClick = (po: PaymentOption) => {
    setSelectedPaymentOption(po);

    // Save token selection for future use
    saveTokenSelectionFromPaymentOption(po.paymentCurrency, po.currencySymbol);

    onSelect(po);
  };

  // Get highest balance token for chain badge
  const highestBalanceToken = paymentOptions?.[0];

  return (
    <>
      <ModalBackButton onClick={() => onBack()} />

      <TransactionCheckoutHeader
        title={
          depositConfig.mode === "pay" || depositConfig.mode === "call"
            ? "Payment token"
            : depositConfig.mode === "deposit"
              ? "Deposit token"
              : "Transfer token"
        }
      />

      <SectionTitle
        title="Choose a token"
        subtitle={
          depositConfig.mode === "deposit"
            ? "Deposit with any token, we'll handle the conversion"
            : "Use any token, we'll handle the conversion"
        }
      />

      <div className="mt-2 space-y-2">
        {paymentOptions.slice(0, 3).map((po) => (
          <PaymentOptionButton
            key={po.paymentCurrency}
            po={po}
            loading={
              selectedPaymentOption?.paymentCurrency === po.paymentCurrency
            }
            showBalance={
              paymentMethod === "wallet" || paymentMethod === "coinbase_app"
            }
            onClick={() => onClick(po)}
          />
        ))}

        {expandTokenList
          ? paymentOptions
              .slice(3)
              .map((po) => (
                <PaymentOptionButton
                  key={po.paymentCurrency}
                  po={po}
                  loading={
                    selectedPaymentOption?.paymentCurrency ===
                    po.paymentCurrency
                  }
                  showBalance={
                    paymentMethod === "wallet" ||
                    paymentMethod === "coinbase_app"
                  }
                  onClick={() => onClick(po)}
                />
              ))
          : null}

        {paymentOptions.length > 3 ? (
          <button
            onClick={() => setExpandTokenList(!expandTokenList)}
            className="flex gap-0.5 text-[--color-text-secondary] hover:text-[--color-text-primary] transition-colors text-xs mb-2"
          >
            {expandTokenList ? (
              <>
                <ChevronUp className="h-4 w-4" /> Show fewer options
              </>
            ) : (
              <>
                <ChevronDown className="h-4 w-4" /> Show{" "}
                {paymentOptions.length - 3 === 1
                  ? "1 more option"
                  : `${paymentOptions.length - 3} more options`}
              </>
            )}
          </button>
        ) : null}
      </div>

      {connectedWallet && (
        <>
          <div className="mt-6 mb-3 border-t border-[--color-background-secondary]" />
          <div className="text-xs text-[--color-text-secondary] mb-2">
            Paying from
          </div>
          <div className="flex items-center gap-3 px-2 py-2 rounded-lg border border-[--color-background-secondary]">
            <div className="flex gap-2.5 items-center flex-1 min-w-0">
              <div className="flex-shrink-0">
                {connectedWallet.icon ? (
                  <LogoWithBadge
                    logo={connectedWallet.icon}
                    badgeUrl={highestBalanceToken?.chainLogoUrl}
                    size="md"
                  />
                ) : (
                  <EVMWalletsLogo className="h-8 w-8" />
                )}
              </div>
              <div className="text-[--color-text-secondary] text-xs truncate">
                {formatShortWalletAddress(connectedWallet.address)}
              </div>
            </div>
            <DisconnectWalletButton
              onDisconnect={handleDisconnect}
              iconSize="sm"
            />
          </div>
        </>
      )}
    </>
  );
}

function PaymentOptionButton({
  po,
  loading,
  showBalance,
  onClick,
}: {
  po: PaymentOption;
  loading?: boolean;
  showBalance?: boolean;
  onClick: () => void;
}) {
  const isBalanceLoaded =
    po.balance !== undefined &&
    po.balance !== null &&
    po.balanceUSD !== undefined &&
    po.balanceUSD !== null;

  return (
    <SecondaryButton
      key={po.paymentCurrency}
      icon={
        <LogoWithBadge
          logoUrl={po.currencyLogoUrl}
          badgeUrl={po.chainLogoUrl}
        />
      }
      title={po.currencyName}
      subtitle={
        showBalance ? (
          isBalanceLoaded ? (
            `${formatDisplayAmount(po.balance)} ${po.currencySymbol} available`
          ) : (
            <div className="animate-pulse bg-[--color-text-secondary] h-4 w-32 opacity-20 rounded"></div>
          )
        ) : (
          ""
        )
      }
      onClick={() => onClick()}
      loading={loading}
      accessory={
        showBalance ? (
          <div className="text-[--color-text-primary] font-medium text-end min-w-[56px]">
            {isBalanceLoaded ? (
              <div className="text-xs">
                $
                {Number(po.balanceUSD).toLocaleString(undefined, {
                  minimumFractionDigits: 2,
                  maximumFractionDigits: 2,
                })}
              </div>
            ) : (
              <div className="animate-pulse bg-[--color-text-secondary] h-4 w-12 opacity-20 rounded"></div>
            )}
            {/* Temporarily disabled for testing
            {checkBalance && !po.hasSufficientBalance ? (
              <div className="mt-0.5 text-[9px] text-[--color-alert-warning-accent]">
                Insufficient balance
              </div>
            ) : null}
            */}
          </div>
        ) : null
      }
      disabled={loading /* || (checkBalance && !po.hasSufficientBalance) */}
    ></SecondaryButton>
  );
}
